import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalesService } from '@/services/sales.service';
import { Opportunity, OpportunityStage } from '@/models/opportunity.model';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FormInput, FormSelect, FormTextarea, LoadingSpinner, Card } from '@/components/common';
import { Target, Building2, TrendingUp, DollarSign, Calendar, Percent, FileText, ArrowLeft, Save } from 'lucide-react';

const opportunityStages: OpportunityStage[] = ['prospecting', 'qualification', 'proposal', 'negotiation', 'closed-won', 'closed-lost'];

export const OpportunityForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Opportunity>>({
    accountId: '',
    name: '',
    stage: 'prospecting',
    value: 0,
    probability: 50,
    expectedCloseDate: new Date(),
    assignedTo: profile?.id || '',
    notes: '',
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => SalesService.getAccounts(),
  });

  const { data: opportunity, isLoading } = useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => id ? SalesService.getOpportunity(id) : null,
    enabled: isEditing && !!id,
  });

  React.useEffect(() => {
    if (opportunity) {
      setFormData(opportunity);
    }
  }, [opportunity]);

  const mutation = useMutation({
    mutationFn: async (data: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (isEditing && id) {
        await SalesService.updateOpportunity(id, data);
      } else {
        await SalesService.createOpportunity(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      navigate('/sales');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const closeDate = formData.expectedCloseDate instanceof Date 
      ? formData.expectedCloseDate 
      : new Date(formData.expectedCloseDate || new Date());
    
    await mutation.mutateAsync({
      ...formData,
      expectedCloseDate: closeDate,
    } as Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>);
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner text="Loading opportunity..." />
      </div>
    );
  }

  const expectedValue = (formData.value || 0) * ((formData.probability || 0) / 100);

  const accountOptions = [
    { value: '', label: 'Select account' },
    ...accounts.map(account => ({
      value: account.id,
      label: account.companyName,
    })),
  ];

  const stageOptions = opportunityStages.map(stage => ({
    value: stage,
    label: stage.charAt(0).toUpperCase() + stage.slice(1).replace('-', ' '),
  }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/sales')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-8 h-8 text-blue-600" />
            {isEditing ? 'Edit Opportunity' : 'New Opportunity'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update opportunity details' : 'Create a new sales opportunity'}
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormInput
                label="Opportunity Name"
                icon={Target}
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Q1 Enterprise Deal"
              />
            </div>

            <FormSelect
              label="Account"
              icon={Building2}
              required
              options={accountOptions}
              value={formData.accountId}
              onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
            />

            <FormSelect
              label="Stage"
              icon={TrendingUp}
              options={stageOptions}
              value={formData.stage}
              onChange={(e) => setFormData({ ...formData, stage: e.target.value as OpportunityStage })}
            />

            <FormInput
              label="Value ($)"
              type="number"
              icon={DollarSign}
              required
              step="0.01"
              min="0"
              value={formData.value || ''}
              onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
              placeholder="0.00"
            />

            <div>
              <FormInput
                label="Probability (%)"
                type="number"
                icon={Percent}
                min="0"
                max="100"
                value={formData.probability || ''}
                onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                placeholder="50"
              />
              <p className="mt-2 text-sm font-medium text-blue-600">
                Expected value: ${expectedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <FormInput
              label="Expected Close Date"
              type="date"
              icon={Calendar}
              required
              value={formData.expectedCloseDate instanceof Date 
                ? formData.expectedCloseDate.toISOString().split('T')[0]
                : (formData.expectedCloseDate ? new Date(formData.expectedCloseDate).toISOString().split('T')[0] : '')}
              onChange={(e) => setFormData({ 
                ...formData, 
                expectedCloseDate: new Date(e.target.value) 
              })}
            />
          </div>

          <FormTextarea
            label="Notes"
            icon={FileText}
            rows={4}
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Add any additional notes about this opportunity..."
          />

          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-900">Opportunity Summary</p>
                <p className="mt-2 text-lg font-bold text-blue-700">
                  ${(formData.value || 0).toLocaleString()} × {formData.probability || 0}% = 
                  <span className="ml-2">${expectedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-blue-400 opacity-50" />
            </div>
          </Card>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/sales')}
              icon={ArrowLeft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Opportunity' : 'Create Opportunity'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

